import React, { useEffect, useState, useCallback } from "react";

import './App.css';

import { get, authorizationIsValid } from './utils/Api';
import Snackbar, { SEVERITY } from './utils/Snackbar';
import { saveJWT, saveAuthenticated, getAuthenticated, getUsuarioPadrao, deleteStorage } from './utils/LocalStorage';

import Main from './utils/Main';
import Error from './utils/Error';
import { createTheme } from '@mui/material/styles';
import { useTheme, ThemeProvider } from '@emotion/react';

import CssBaseline from '@mui/material/CssBaseline';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

const FRONTEND_VERSION = process.env.REACT_APP_FRONTEND_VERSION;

function App() {
  const breakpointOverrides = {
    xs: 0,
    sm: 900,
    md: 900,
    lg: 1250,
    xl: 1536,
  };

  const baseTheme = createTheme();

  const getCustomTheme = (theme) =>
    createTheme({
      ...theme,
      breakpoints: { values: { ...breakpointOverrides } },
      palette: {
        background: {
          default: '#eee',
        },
        secondary: {
          main: '#CE8786',
          dark: '#CE8786',
        },
        branca: baseTheme.palette.augmentColor({
          color: {
            main: '#fff',
            dark: '#fff',
          }
        }),
        elaboracao: baseTheme.palette.augmentColor({
          color: {
            main: '#e0e0e0',
            dark: '#e0e0e0',
          }
        }),
        preenchido: baseTheme.palette.augmentColor({
          color: {
            main: '#039be5',
            dark: '#039be5',
          }
        }),
        incompleto: baseTheme.palette.augmentColor({
          color: {
            main: '#f4511e',
            dark: '#f4511e',
          }
        }),
        revisado: baseTheme.palette.augmentColor({
          color: {
            main: '#43a047',
            dark: '#43a047',
          }
        }),
        alterado: baseTheme.palette.augmentColor({
          color: {
            main: '#8e24aa',
            dark: '#8e24aa',
          }
        }),
        ativo: baseTheme.palette.augmentColor({
          color: {
            main: '#43a047',
            dark: '#43a047',
          }
        }),
        inativo: baseTheme.palette.augmentColor({
          color: {
            main: '#f4511e',
            dark: '#f4511e',
          }
        }),
        azul: baseTheme.palette.augmentColor({
          color: {
            main: '#1565c0',
            dark: '#1565c0',
          }
        }),
        alerta: baseTheme.palette.augmentColor({
          color: {
            main: '#ffa726',
            dark: '#ffa726',
          }
        }),
        branco: baseTheme.palette.augmentColor({
          color: {
            main: '#ffffff',
            dark: '#ffffff',
          }
        }),
      },
    });

  const theme = useTheme();

  const [error, setError] = useState(false);
  const [paginaErro, setPaginaErro] = useState('');

  const [basicData,setBasicData] = useState({
    version: '0.00.00 DEV',
  });

  const [usuario, setUsuario] = useState(getUsuarioPadrao);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileUsuariosOpen, setMobileUsuariosOpen] = useState(false);

  const [snackbar,setSnackbar] = useState({
    open: false,
    message: '',
    severity: SEVERITY.INFO.toString(),
  });

  const [loading, setLoading] = useState(true);

  const printSafeJson = (json,callback) => {
    if ((json !== undefined) &&  (json !== null) && (json !== '')) {
      const list = ['usuario_senha','senha correta','usuario_senha_confirmar','password','password_bytes','plain_text_password','hashed_password',]
      list.forEach((entry, index) => {
          if (entry in json) {
            delete json[entry];
          }

          if (index === (list.length - 1)) {
            console.error(json);
            callback();
          }
      });
    } else {
      callback();
    }
  };

  const handleMessage = useCallback((message,severity,error,exibirErro) => {
    let exibirPaginaErro = false;
    if ( (exibirErro === undefined) || (exibirErro === null) || (exibirErro === true) ) {
      exibirPaginaErro = true;
    }

    setSnackbar(estadoAnterior => ({
      ...estadoAnterior,
      open: true,
      message: message,
      severity: severity.toString()
    }));

    printSafeJson(error,()=>{
      if ( (severity === SEVERITY.ERROR.toString()) || (severity === SEVERITY.WARNING.toString()) ) {
        if (exibirPaginaErro) {
          setPaginaErro(message);
          setError(true);
        }
      }
    });
  }, []);

  const handleCloseMessage = () => {
    setSnackbar(estadoAnterior => ({
      ...estadoAnterior,
      open: false
    }));
  };

  const changeUsuario = useCallback((usuario,session_token,callback) => {
    if ( (usuario === undefined) || (usuario === null) || !('usuario_uuid' in usuario) ) {
      usuario = getUsuarioPadrao();
    }

    saveAuthenticated(usuario,()=>{
      setUsuario((dados) => ({
        ...dados,
        usuario_uuid: usuario.usuario_uuid,
        usuario_email: usuario.usuario_email,
        usuario_nome: usuario.usuario_nome,
        usuario_ativo: usuario.usuario_ativo,
        usuario_nome_social: usuario.usuario_nome_social,
        usuario_administrador: usuario.usuario_administrador,
        perfil_uuid: usuario.perfil_uuid,
        perfil_nome: usuario.perfil_nome,
        session_uuid: usuario.session_uuid,
        session_logado: usuario.session_logado,
        escritorio_uuid: usuario.escritorio_uuid,
        escritorio_nome: usuario.escritorio_nome,
        escritorio_ativo: usuario.escritorio_ativo,
      }));
      if (authorizationIsValid(session_token)) {
        saveJWT(session_token);
      }
      callback();
    });
  }, []);

  useEffect(()=>{
    if (error) { return; }

    const validarJson = (json) => {
      return (
        (json !== undefined) &&
        (json !== null) &&
        (json.usuario !== undefined) &&
        (json.usuario !== null) &&
        (json.usuario.session_logado) &&
        (json.usuario.usuario_ativo) &&
        (json.session_token !== undefined) &&
        (json.session_token !== null) &&
        (json.session_token !== '') &&
        (json.session_token !== undefined) &&
        (json.escritorio !== undefined) &&
        (json.escritorio !== null) &&
        (json.escritorio.escritorio_uuid !== undefined) &&
        (json.escritorio.escritorio_uuid !== null) &&
        (json.escritorio.escritorio_uuid !== '') &&
        (json.escritorio.escritorio_ativo)
      )
    };

    getAuthenticated((usuario)=>{
      usuario.escritorio_uuid

      get('api/dados-basicos',(json)=>{
        const version = `v${json.version.main_version}.${FRONTEND_VERSION}.${json.version.backend_version}${json.version.environment}`;
        console.log(version);
        setBasicData(estadoAnterior => ({
          ...estadoAnterior,
          version: version
        }));

        get(`api/usuario`,(json)=>{

          if (validarJson(json)) {

            if (json.escritorio.escritorio_ativo) {
              changeUsuario(json.usuario,json.session_token,()=>{
                console.log(`Usuário logado: ${json.usuario.usuario_nome_social} [${json.usuario.usuario_uuid.substring(0,6)}]`);
                setLoading(false);
              });
            } else {
              handleMessage(`Seu escritório está inativo no sistema!`,SEVERITY.WARNING.toString(),'',false);
            }
          } else {
            deleteStorage();
            setLoading(false);
          }

        },(error,message,severity)=>{
          setLoading(false);
          handleMessage(message,severity,error,true);
        });

      },(error,message,severity)=>{
        setLoading(false);
        handleMessage(message,severity,error,true);
      });
    });

  },[]);

  return (
    <ThemeProvider theme={getCustomTheme(theme)}>
      <CssBaseline />
      <Snackbar
        message={snackbar.message}
        severity={snackbar.severity}
        opened={snackbar.open}
        handleClose={handleCloseMessage}
      />

      {
        (paginaErro !== '') ? (
          <Error
            version={basicData.version}
            setLoading={setLoading}
            handleMessage={handleMessage}
            error={error}
            setError={setError}
            naoExibirMenu={true}
            mensagem={paginaErro}
          />
        ) : (
          <Main
            setLoading={setLoading}
            handleMessage={handleMessage}

            paginaErro={paginaErro}
            error={error}
            setError={setError}
            version={basicData.version}

            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            mobileUsuariosOpen={mobileUsuariosOpen}
            setMobileUsuariosOpen={setMobileUsuariosOpen}

            usuario={usuario}
            changeUsuario={changeUsuario}
          />
        )
      }

      <Backdrop
        sx={() => ({
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 2000
        })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
  );
}

export default App;
